import React, { useState } from "react";
import { toast } from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import api from "../../utils/api";
import { useAuth } from "../../context/AuthContext";
import "../../pages/account/AccountPage.css";

const DangerZoneTab = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();

  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmText, setConfirmText] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // ─── Deactivate ───────────────────────────────────────────
  const handleDeactivate = async () => {
    if (!password) return toast.error("Please enter your password");
    try {
      setIsLoading(true);
      await api.put("/users/deactivate", { password });
      toast.success("Account deactivated. You have been logged out.");
      logout();
      navigate("/login");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to deactivate account");
    } finally {
      setIsLoading(false);
    }
  };

  // ─── Delete ───────────────────────────────────────────────
  const handleDelete = async () => {
    if (!password) return toast.error("Please enter your password");
    if (confirmText !== "DELETE") return toast.error('Type DELETE to confirm');
    try {
      setIsLoading(true);
      await api.delete("/users/delete-account", { data: { password } });
      toast.success("Account permanently deleted.");
      logout();
      navigate("/");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to delete account");
    } finally {
      setIsLoading(false);
    }
  };

  const resetModal = () => {
    setPassword("");
    setConfirmText("");
    setShowDeactivateModal(false);
    setShowDeleteModal(false);
  };

  return (
    <div className="account-profile-card">
      {/* HEADER */}
      <div className="profile-header-column">
        <h3>Danger Zone</h3>
        <p className="sub-text">
          Irreversible actions — please read carefully before proceeding.
        </p>
      </div>

      {/* DEACTIVATE */}
      <div className="danger-zone-item">
        <div className="danger-zone-info">
          <h4>Deactivate Account</h4>
          <p>
            Your account will be hidden and disabled. You can reactivate it by
            logging back in and contacting support.
          </p>
        </div>
        <button
          className="danger-btn warning"
          onClick={() => setShowDeactivateModal(true)}
        >
          Deactivate Account
        </button>
      </div>

      <hr className="danger-divider" />

      {/* DELETE */}
      <div className="danger-zone-item">
        <div className="danger-zone-info">
          <h4>Delete Account</h4>
          <p>
            Permanently removes your account and all associated data. This
            action <strong>cannot be undone</strong>.
          </p>
        </div>
        <button
          className="danger-btn delete"
          onClick={() => setShowDeleteModal(true)}
        >
          Delete Account
        </button>
      </div>

      {/* ── DEACTIVATE MODAL ── */}
      {showDeactivateModal && (
        <div className="modal-overlay" onClick={resetModal}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <h3>Deactivate your account?</h3>
            <p>
              Your profile will be hidden until you reactivate. Enter your
              password to continue.
            </p>
            <div className="dz-password-input">
              <input
                type="password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
            <div className="modal-actions">
              <button className="dz-modal-cancel-btn" onClick={resetModal}>
                Cancel
              </button>
              <button
                className="danger-btn warning"
                onClick={handleDeactivate}
                disabled={isLoading || !password}
              >
                {isLoading ? <span className="spinner" /> : "Yes, Deactivate"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── DELETE MODAL ── */}
      {showDeleteModal && (
        <div className="modal-overlay" onClick={resetModal}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <h3>Permanently delete your account?</h3>
            <p>
              All your data will be erased and cannot be recovered. Type{" "}
              <strong>DELETE</strong> below and enter your password to confirm.
            </p>
            <input
              type="text"
              placeholder='Type DELETE to confirm'
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              className="confirm-text-input"
            />
            <div className="dz-password-input" style={{ marginTop: "12px" }}>
              <input
                type="password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
            <div className="modal-actions">
              <button className="dz-modal-cancel-btn" onClick={resetModal}>
                Cancel
              </button>
              <button
                className="danger-btn delete"
                onClick={handleDelete}
                disabled={isLoading || !password || confirmText !== "DELETE"}
              >
                {isLoading ? <span className="spinner" /> : "Yes, Delete Permanently"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DangerZoneTab;